import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  FiZap,
  FiSearch,
  FiPenTool,
  FiMessageSquare,
  FiSend,
} from "react-icons/fi";

export default function AboutSection() {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.15, 0.85, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.15], [0.96, 1]);

  const team = [
    {
      role: "Founders & CEO",
      members: "Ayyanar & Suguna",
      desc: "Driving Aysu Groups’ vision and strategic direction.",
      gradient: "from-teal-400 to-emerald-500",
    },
    {
      role: "Managing Directors",
      members: "Sabari Giri Vasan & Kaviya Akshaya",
      desc: "Leading operations and digital transformation delivery.",
      gradient: "from-blue-400 to-indigo-500",
    },
  ];

  const workflow = [
    {
      icon: <FiSearch size={20} />,
      title: "Requirement",
      desc: "Understand client goals & scope.",
    },
    {
      icon: <FiPenTool size={20} />,
      title: "Planning",
      desc: "Design + development roadmap.",
    },
    {
      icon: <FiMessageSquare size={20} />,
      title: "Feedback",
      desc: "Iteration & refinement cycles.",
    },
    {
      icon: <FiSend size={20} />,
      title: "Delivery",
      desc: "Final product deployment.",
    },
  ];

  return (
    <section
      ref={containerRef}
      id="about"
      className="relative py-16 bg-[#022c22] text-white overflow-hidden"
    >
      {/* noise background */}
      <div className="absolute inset-0 opacity-20 bg-[url('https://grainy-gradients.vercel.app/noise.svg')]" />

      <motion.div style={{ opacity, scale }} className="relative z-10 max-w-7xl mx-auto px-6">

        {/* HEADER (compact) */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-teal-500/30 bg-teal-500/10 text-teal-400 text-[10px] tracking-[0.3em] uppercase mb-4">
            <FiZap /> Aysu Standard
          </div>

          <h2 className="text-4xl md:text-6xl font-black leading-tight mb-4">
            Ambition Meets{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-500">
              Execution
            </span>
          </h2>

          <p className="text-slate-400 max-w-2xl mx-auto text-sm md:text-base">
            We design, build and scale modern digital systems for businesses.
          </p>
        </div>

        {/* TEAM */}
        <div className="grid md:grid-cols-2 gap-6 mb-16">
          {team.map((item, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -6 }}
              className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl"
            >
              <div className={`text-xs font-bold uppercase bg-gradient-to-r ${item.gradient} text-transparent bg-clip-text mb-2`}>
                {item.role}
              </div>
              <h3 className="text-xl font-bold mb-2">{item.members}</h3>
              <p className="text-sm text-slate-400">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* divider */}
        <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-teal-500/40 to-transparent mb-14" />

        {/* WORKFLOW */}
        <div>
          <div className="text-center mb-10">
            <h3 className="text-3xl md:text-4xl font-black mb-2">
              Workflow
            </h3>
            <p className="text-slate-400 text-sm">
              Simple, structured, and result-driven process
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-5">
            {workflow.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -8 }}
                transition={{ delay: i * 0.1 }}
                className="relative p-5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl overflow-hidden"
              >
                {/* step */}
                <div className="absolute top-3 right-4 text-4xl font-black text-white/5">
                  0{i + 1}
                </div>

                {/* icon */}
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-teal-500/10 text-teal-400 mb-3">
                  {item.icon}
                </div>

                <h4 className="font-bold mb-1">{item.title}</h4>
                <p className="text-xs text-slate-400">{item.desc}</p>

                {/* bottom glow line */}
                <div className="absolute bottom-0 left-4 right-4 h-[1px] bg-gradient-to-r from-transparent via-teal-500/40 to-transparent opacity-0 hover:opacity-100 transition" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* IMPACT STRIP (NEW PREMIUM FEATURE) */}
        <div className="mt-14 grid grid-cols-3 text-center gap-4">
          {[
            { label: "Design First", value: "UI/UX" },
            { label: "Performance", value: "FAST" },
            { label: "Affordable", value: "COST" },
          ].map((item, i) => (
            <div key={i} className="text-xs text-slate-400">
              <div className="text-teal-400 font-bold text-sm">
                {item.value}
              </div>
              {item.label}
            </div>
          ))}
        </div>

      </motion.div>
    </section>
  );
}